import { action, makeObservable, observable, runInAction, computed } from 'mobx';
import type {
  EffectiveBlockDefaultsType,
  IDashboardBlock,
  IPageShares,
  IPageSharingOptions,
  IWorkAreaDashboardPage,
  IWorkAreaDashboardPageCheckpoint,
  IWorkAreaDashboardPageSnapshot,
} from '@featit/shared';
import {
  type ProjectContentDataSourceRegistry,
  projectContentDataSourceRegistry,
} from '@/data-sources/ProjectContentDataSourceRegistry';
import { compareDatasetCheckpoints, DatasetSyncController } from '@/local-sync/datasetSync';
import { localSyncStoreFor } from '@/local-sync/localSyncStoreFor';
import { failureToSay, type HeldSentenceType, pageWords, sayEach } from '@/services/actionFailure';
import { settleWithin } from '@/utils/accessReadDeadline';
import type { IAccessRead } from '@/utils/workAreaPermissionGate';

/**
 * Where the page list was cached on the device until I-1895. Nothing reads or writes it now: which
 * pages a reader is shown is the server's answer for them, and a copy kept titles at rest that the
 * reader may have lost. Each work area's record is removed when its pages are first synced.
 */
const RETIRED_DASHBOARD_PAGE_CACHE_COLLECTION = 'work-area-dashboard-pages';
const DASHBOARD_PAGE_SYNC_INTERVAL_MS = 30_000;

export class DashboardPageStore {
  pagesByProjectId: Record<string, IWorkAreaDashboardPage[]> = {};
  checkpointsByProjectId: Record<string, IWorkAreaDashboardPageCheckpoint | undefined> = {};
  canManageByProjectId: Record<string, boolean | undefined> = {};
  blockDefaultsByProjectId: Record<string, EffectiveBlockDefaultsType | undefined> = {};
  /** What a page may require and be shared with, sent to a reader who manages pages (T-0783). */
  sharingByProjectId: Record<string, IPageSharingOptions | undefined> = {};
  loadingProjectIds: string[] = [];
  /** T-0471: what `errorsByProjectId` says, kept, so it is written in the page's language when read. */
  heldErrorsByProjectId: Record<string, HeldSentenceType | undefined> = {};
  get errorsByProjectId(): Record<string, string | undefined> {
    const held = this.heldErrorsByProjectId;
    return sayEach(pageWords(), held);
  }
  reorderingProjectIds: string[] = [];
  /** T-0471: what `orderErrorsByProjectId` says, kept, so it is written in the page's language when read. */
  heldOrderErrorsByProjectId: Record<string, HeldSentenceType | undefined> = {};
  get orderErrorsByProjectId(): Record<string, string | undefined> {
    const held = this.heldOrderErrorsByProjectId;
    return sayEach(pageWords(), held);
  }
  /** Work areas whose unread manage answer is being read again (I-1708, KD-17a). */
  rereadingProjectIds: string[] = [];

  private readonly controllers = new Map<string, DatasetSyncController>();
  private readonly requestSequences = new Map<string, number>();

  constructor(
    private readonly dataSources: ProjectContentDataSourceRegistry = projectContentDataSourceRegistry,
  ) {
    makeObservable(this, {
      pagesByProjectId: observable,
      checkpointsByProjectId: observable,
      canManageByProjectId: observable,
      blockDefaultsByProjectId: observable,
      sharingByProjectId: observable,
      loadingProjectIds: observable,
      heldErrorsByProjectId: observable,
      errorsByProjectId: computed,
      reorderingProjectIds: observable,
      heldOrderErrorsByProjectId: observable,
      orderErrorsByProjectId: computed,
      rereadingProjectIds: observable,
      dismissOrderError: action,
      ensureProject: action,
      refresh: action,
      createPage: action,
      updatePage: action,
      movePage: action,
      removePage: action,
    });
  }

  pages(projectId?: string | null): IWorkAreaDashboardPage[] {
    return projectId ? (this.pagesByProjectId[projectId] ?? []) : [];
  }

  /** The block defaults in force for a work area (F-0195); `undefined` until a snapshot arrived. */
  blockDefaults(projectId?: string | null): EffectiveBlockDefaultsType | undefined {
    return projectId ? this.blockDefaultsByProjectId[projectId] : undefined;
  }

  /** What a page may require and be shared with; empty for a reader who cannot choose (T-0783). */
  sharingOptions(projectId?: string | null): IPageSharingOptions {
    return (
      (projectId ? this.sharingByProjectId[projectId] : undefined) ?? {
        roles: [],
        users: [],
        permissions: [],
      }
    );
  }

  canManage(projectId?: string | null): boolean {
    return projectId ? this.canManageByProjectId[projectId] === true : false;
  }

  /**
   * The server's answer to "may this reader manage the pages here", or `undefined` while it has not
   * arrived — so a gated control can be pending rather than refused for that moment (F-0324).
   */
  manageAnswer(projectId?: string | null): boolean | undefined {
    return projectId ? this.canManageByProjectId[projectId] : undefined;
  }

  /**
   * The read `manageAnswer` comes from, for `heldGate` (I-1702, KD-17): settled once a snapshot was
   * applied or the read failed, and asked again by `retry`. A failed read, or a snapshot without a
   * boolean `canManage`, leaves the answer unread rather than pending for good.
   */
  manageRead(projectId?: string | null): IAccessRead {
    if (!projectId) return { settled: false };
    // KD-17a (I-1708): a re-read of an unread answer is a retry in flight — checking again.
    const retrying = this.rereadingProjectIds.includes(projectId);
    return {
      // Every applied snapshot writes its `canManage` slot, whatever shape the answer had.
      settled:
        retrying ||
        projectId in this.canManageByProjectId ||
        Boolean(this.heldErrorsByProjectId[projectId]),
      retrying,
      retry: () => void this.refresh(projectId).catch(() => undefined),
    };
  }

  isReordering(projectId?: string | null): boolean {
    return Boolean(projectId && this.reorderingProjectIds.includes(projectId));
  }

  orderError(projectId?: string | null): string | undefined {
    return projectId ? this.orderErrorsByProjectId[projectId] : undefined;
  }

  dismissOrderError(projectId: string): void {
    this.heldOrderErrorsByProjectId = {
      ...this.heldOrderErrorsByProjectId,
      [projectId]: undefined,
    };
  }

  isLoading(projectId?: string | null): boolean {
    return Boolean(projectId && this.loadingProjectIds.includes(projectId));
  }

  ensureProject(projectId: string): void {
    if (this.controllers.has(projectId)) return;
    // I-1895: drop the titles an earlier version left on this device. `remove` swallows a failure.
    void localSyncStoreFor(projectId).remove(RETIRED_DASHBOARD_PAGE_CACHE_COLLECTION, projectId);
    const controller = new DatasetSyncController({
      intervalMs: DASHBOARD_PAGE_SYNC_INTERVAL_MS,
      // No hydrate from the device's cache (I-1895): which pages a reader is shown depends on their
      // roles and shares, and the cached list is someone's earlier answer. A page they have since
      // lost would show its title until the server answered — and the checkpoint is the work area's,
      // not the reader's, so an unchanged one never asked again. The first pass is the server read.
      verify: () => this.verify(projectId),
      onError: (cause) => {
        runInAction(() => {
          this.heldErrorsByProjectId = {
            ...this.heldErrorsByProjectId,
            [projectId]: errorMessage(cause),
          };
          this.loadingProjectIds = this.loadingProjectIds.filter((id) => id !== projectId);
        });
      },
    });
    this.controllers.set(projectId, controller);
    controller.start();
  }

  async refresh(projectId: string): Promise<void> {
    await this.loadSnapshot(projectId);
  }

  async createPage(
    projectId: string,
    title: string,
    parentPageId: string | null = null,
  ): Promise<IWorkAreaDashboardPage> {
    const created = await this.dataSources
      .forProject(projectId)
      .createWorkAreaDashboardPage(projectId, { title, parentPageId });
    await this.loadSnapshot(projectId);
    return created;
  }

  /**
   * Moves a page under another page, or to the top level (F-0201).
   *
   * The move shows at once and the save follows, for the reason a reorder does: a tree that only
   * redraws when the server answers reads as a drag that did not take. A refused move puts back
   * exactly the tree that was on screen and says why — the server refuses a cycle and an over-deep
   * move naming both pages, so its own sentence is the one worth showing.
   */
  async movePage(projectId: string, pageId: string, parentPageId: string | null): Promise<boolean> {
    const previous = this.pages(projectId);
    const moved = previous.map((page) => (page.id === pageId ? { ...page, parentPageId } : page));
    this.requestSequences.set(projectId, (this.requestSequences.get(projectId) ?? 0) + 1);
    runInAction(() => {
      this.pagesByProjectId = { ...this.pagesByProjectId, [projectId]: moved };
      this.reorderingProjectIds = [...this.reorderingProjectIds, projectId];
      this.heldOrderErrorsByProjectId = {
        ...this.heldOrderErrorsByProjectId,
        [projectId]: undefined,
      };
    });
    try {
      await this.dataSources
        .forProject(projectId)
        .moveWorkAreaDashboardPage(projectId, pageId, { parentPageId });
      return true;
    } catch (cause) {
      runInAction(() => {
        this.pagesByProjectId = { ...this.pagesByProjectId, [projectId]: previous };
        this.heldOrderErrorsByProjectId = {
          ...this.heldOrderErrorsByProjectId,
          [projectId]:
            httpStatus(cause) === 403
              ? pageWords()('navigation.reorder.store.cannotMovePages')
              : (serverMessage(cause) ?? failureToSay(cause, 'navigation.reorder.store.movePage')),
        };
      });
      return false;
    } finally {
      runInAction(() => {
        this.reorderingProjectIds = this.reorderingProjectIds.filter((id) => id !== projectId);
      });
      void this.loadSnapshot(projectId).catch(() => undefined);
    }
  }

  async updatePage(
    projectId: string,
    pageId: string,
    input: {
      expectedRevision: number;
      title?: string;
      position?: number;
      icon?: string | null;
      audiencePermission?: string | null;
      /** Who the page is shared with, replacing the shares it has; absent leaves them (T-0783). */
      sharedWith?: IPageShares;
      /** AGM-42: the item type the page is opened for one item of; null unscopes it. */
      scopeItemTypeKey?: string | null;
      blocks?: IDashboardBlock[];
    },
  ): Promise<IWorkAreaDashboardPage> {
    const { sharedWith, ...page } = input;
    const source = this.dataSources.forProject(projectId);
    const updated = await source.updateWorkAreaDashboardPage(projectId, pageId, page);
    if (sharedWith) await source.setWorkAreaDashboardPageShares(projectId, pageId, sharedWith);
    await this.loadSnapshot(projectId);
    return updated;
  }

  /**
   * Puts the custom pages in `pageIds` order (F-0186).
   *
   * The new order shows at once and the save follows. A refused save puts back exactly the order
   * that was on screen before, and says why — a list that silently snapped back would read as a
   * drag that did not take. A snapshot already in flight is superseded, so it cannot land on top of
   * the order just drawn with the order from before it.
   */
  async reorderPages(
    projectId: string,
    pageIds: string[],
    parentPageId: string | null = null,
  ): Promise<boolean> {
    const previous = this.pages(projectId);
    const byId = new Map(previous.map((page) => [page.id, page]));
    const ordered = new Set(pageIds);
    // Only the level being ordered changes. Its pages are written back into the slots they already
    // held, in the new order, so every other page — and every other branch — keeps its place: the
    // list the sidebar draws from is the server's order, and a reorder of one level must not
    // reshuffle the rest of it.
    let slot = 0;
    const reordered = previous.map((page) => {
      if (!ordered.has(page.id)) return page;
      const next = byId.get(pageIds[slot]);
      slot += 1;
      return next ? { ...next, position: slot * 10 } : page;
    });
    this.requestSequences.set(projectId, (this.requestSequences.get(projectId) ?? 0) + 1);
    runInAction(() => {
      this.pagesByProjectId = { ...this.pagesByProjectId, [projectId]: reordered };
      this.reorderingProjectIds = [...this.reorderingProjectIds, projectId];
      this.heldOrderErrorsByProjectId = {
        ...this.heldOrderErrorsByProjectId,
        [projectId]: undefined,
      };
    });
    try {
      await this.dataSources
        .forProject(projectId)
        .reorderWorkAreaDashboardPages(projectId, pageIds, parentPageId);
      return true;
    } catch (cause) {
      const conflict = httpStatus(cause) === 409;
      runInAction(() => {
        this.pagesByProjectId = { ...this.pagesByProjectId, [projectId]: previous };
        this.heldOrderErrorsByProjectId = {
          ...this.heldOrderErrorsByProjectId,
          [projectId]: conflict
            ? pageWords()('navigation.reorder.store.pagesChanged')
            : httpStatus(cause) === 403
              ? pageWords()('navigation.reorder.store.cannotReorderPages')
              : pageWords()('navigation.reorder.store.orderNotSaved'),
        };
      });
      return false;
    } finally {
      runInAction(() => {
        this.reorderingProjectIds = this.reorderingProjectIds.filter((id) => id !== projectId);
      });
      // Either way the server's answer is the one to show next: the saved order, or — after a
      // conflict — the pages someone else created or deleted.
      void this.loadSnapshot(projectId).catch(() => undefined);
    }
  }

  async removePage(projectId: string, pageId: string): Promise<void> {
    await this.dataSources.forProject(projectId).removeWorkAreaDashboardPage(projectId, pageId);
    await this.loadSnapshot(projectId);
  }

  stop(): void {
    this.controllers.forEach((controller) => controller.stop());
    this.controllers.clear();
  }

  private async verify(projectId: string): Promise<void> {
    const applied = this.checkpointsByProjectId[projectId];
    if (!applied) {
      await this.loadSnapshot(projectId, { background: true });
      return;
    }
    const remote = await this.dataSources
      .forProject(projectId)
      .workAreaDashboardPageCheckpoint(projectId);
    if (compareDatasetCheckpoints(applied, remote).status !== 'unchanged') {
      await this.loadSnapshot(projectId, { background: true });
    }
  }

  /**
   * `background` is the sync controller's re-read, which nobody asked for (I-1702, reopened on
   * 0.5.16, KD-17c). Once the read has settled — answered, or unread — a background re-read keeps
   * that state while it runs: no error cleared, no loading, no "checking again". It settles again
   * only on its own answer. Clearing the error at its start put a refused New page back to
   * "Checking access…" for each 20 s read that never answered, one after the other, for good.
   */
  private async loadSnapshot(
    projectId: string,
    { background = false }: { background?: boolean } = {},
  ): Promise<void> {
    const sequence = (this.requestSequences.get(projectId) ?? 0) + 1;
    this.requestSequences.set(projectId, sequence);
    const settled =
      projectId in this.canManageByProjectId || Boolean(this.heldErrorsByProjectId[projectId]);
    runInAction(() => {
      if (background && settled) return;
      // The answer this read replaces was unread (failed, or not a boolean): it is a retry.
      const unread =
        typeof this.canManageByProjectId[projectId] !== 'boolean' &&
        (projectId in this.canManageByProjectId || Boolean(this.heldErrorsByProjectId[projectId]));
      if (unread && !this.rereadingProjectIds.includes(projectId)) {
        this.rereadingProjectIds = [...this.rereadingProjectIds, projectId];
      }
      if (!this.pagesByProjectId[projectId] && !this.loadingProjectIds.includes(projectId)) {
        this.loadingProjectIds = [...this.loadingProjectIds, projectId];
      }
      this.heldErrorsByProjectId = { ...this.heldErrorsByProjectId, [projectId]: undefined };
    });
    try {
      // I-1702: a read that never answers fails after the request timeout (`settleWithin`).
      const snapshot = await settleWithin(
        this.dataSources.forProject(projectId).workAreaDashboardPages(projectId),
      );
      if (this.requestSequences.get(projectId) !== sequence) return;
      runInAction(() => this.applySnapshot(projectId, snapshot));
    } catch (cause) {
      if (this.requestSequences.get(projectId) !== sequence) return;
      runInAction(() => {
        this.heldErrorsByProjectId = {
          ...this.heldErrorsByProjectId,
          [projectId]: errorMessage(cause),
        };
        this.loadingProjectIds = this.loadingProjectIds.filter((id) => id !== projectId);
      });
      throw cause;
    } finally {
      // The latest read owns the retry: a superseded one leaves it to the read that replaced it.
      if (this.requestSequences.get(projectId) === sequence) {
        runInAction(() => {
          this.rereadingProjectIds = this.rereadingProjectIds.filter((id) => id !== projectId);
        });
      }
    }
  }

  private applySnapshot(projectId: string, snapshot: IWorkAreaDashboardPageSnapshot): void {
    this.pagesByProjectId = {
      ...this.pagesByProjectId,
      [projectId]: snapshot.items,
    };
    this.checkpointsByProjectId = {
      ...this.checkpointsByProjectId,
      [projectId]: snapshot.checkpoint,
    };
    this.canManageByProjectId = {
      ...this.canManageByProjectId,
      [projectId]: snapshot.canManage,
    };
    this.blockDefaultsByProjectId = {
      ...this.blockDefaultsByProjectId,
      [projectId]: snapshot.blockDefaults,
    };
    this.sharingByProjectId = {
      ...this.sharingByProjectId,
      [projectId]: snapshot.sharing,
    };
    this.loadingProjectIds = this.loadingProjectIds.filter((id) => id !== projectId);
    this.heldErrorsByProjectId = { ...this.heldErrorsByProjectId, [projectId]: undefined };
  }
}

function httpStatus(cause: unknown): number | undefined {
  return (cause as { response?: { status?: number } })?.response?.status;
}

/**
 * What the server said, where it said something worth reading. A refused move names the two pages
 * and why — "…is already inside…", "…at most 5 levels deep…" — and no sentence written here could
 * be as useful as that one.
 */
function serverMessage(cause: unknown): string | undefined {
  const message = (cause as { response?: { data?: { message?: unknown } } })?.response?.data
    ?.message;
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (Array.isArray(message)) {
    const first = message.find((entry) => typeof entry === 'string' && entry.trim());
    if (typeof first === 'string') return first.trim();
  }
  return undefined;
}

function errorMessage(cause: unknown): HeldSentenceType {
  return failureToSay(cause, 'navigation.reorder.store.syncPages');
}
